import {
  CondensedButton,
  Flex,
  IconArchiveLine,
  IconButton,
  IconEditLine,
  IconRubricLine,
  IconTrashLine,
  Pill,
  Text,
  ToggleDetails,
  Tooltip,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useMemo, useState } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { LearningOutcomesService } from "../../../services/LearningOutcomesService";
import { MasteryLevelContext } from "../../../utils/Context";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";
import EditOutcomeModal from "../../Outcomes/Review/EditOutcomeModal";
import RubricMappingModal from "../../Outcomes/Review/RubricMappingModal";
import { getEarliestCanvasTerm } from "../../TermManagement/utils";
import ApproveDialog from "../ApproveDialog";
import ArchiveModal from "../ArchiveModal";
import {
  getCurrentVersion,
  getFutureVersions,
  getPastVersions,
  hasUnmappedRatingScale,
  pendingArchive,
} from "../utils";
import { CategoriesContext, UncategorizedContext } from "./CategoryList";

const PLOCard = ({
  outcome,
  setLoadingPlos,
  programId,
  handleArchiveOutcome,
  handleUpdateRatingScale,
}) => {
  const { t } = useTranslation();
  const { categories, setCategories } = useContext(CategoriesContext);
  const { uncategorizedLos, setUncategorizedLos } =
    useContext(UncategorizedContext);
  const showNotification = useNotification();
  const { ratingScales } = useContext(MasteryLevelContext);
  const [approveDialog, setApproveDialog] = useState(false);
  const [deleteOutcomeOpen, setDeleteOutcomeOpen] = useState(false);
  const [versionOptions, setVersionOptions] = useState([]);
  const futureVersions = useMemo(() => getFutureVersions(outcome), [outcome]);
  const pastVersions = useMemo(() => getPastVersions(outcome), [outcome]);
  const currentVersion = useMemo(() => getCurrentVersion(outcome), [outcome]);
  const [rubricTrigger, setRubricTrigger] = useState(false);

  useEffect(() => {
    if (rubricTrigger) {
      LearningOutcomesService.getLearningOutcomeById(outcome.id).then((lo) => {
        handleUpdateRatingScale(lo);
        setRubricTrigger(false);
      });
    }
  }, [rubricTrigger]);

  useEffect(() => {
    let versions;
    if (outcome.category_id) {
      const catIndex = categories.findIndex(
        (c) => c.id === outcome.category_id
      );
      versions = categories[catIndex].outcomes.filter(
        (o) => !o.unapproved && o.id !== outcome.id
      );
    } else {
      versions = uncategorizedLos.filter(
        (o) => !o.unapproved && o.id !== outcome.id
      );
      let categoryOutcomes = categories.map((cat) => {
        return cat.outcomes.filter((o) => !o.unapproved && o.id !== outcome.id);
      });
      categoryOutcomes = categoryOutcomes.flat();
      versions = versions.concat(categoryOutcomes);
    }

    versions = versions.filter(
      (o) => o.outcome_variety === outcome.outcome_variety
    );
    versions = versions.map((o) => ({ ...o, label: o.name }));

    setVersionOptions(versions);
  }, [categories, uncategorizedLos, outcome]);

  const handleSaveOutcome = async (
    newName,
    newStatement,
    outcomeVariety,
    version,
    effectiveTerm
  ) => {
    try {
      let earliestCanvasTerm = undefined;
      if (effectiveTerm) {
        earliestCanvasTerm = getEarliestCanvasTerm(effectiveTerm);
      }

      await LearningOutcomesService.updateLearningOutcome({
        id: currentVersion.id,
        name: newName,
        seq: outcome.sequence,
        statement: newStatement,
        programId: programId,
        categoryId: outcome.category_id,
        outcomeType: outcome.outcome_type,
        outcomeVariety: outcomeVariety,
        relatedOutcomeId: futureVersions[0]?.id ?? currentVersion.id,
        newVersion: version,
        effectiveTermId: earliestCanvasTerm?.id,
      });
      setLoadingPlos(true);
      showNotification(
        t("notifications.general.updateSuccess", {
          obj: t("plo.programLearningOutcome"),
        })
      );
    } catch {
      throw new Error(
        t("notifications.general.updateError", {
          obj: t("plo.programLearningOutcome"),
        })
      );
    }
  };

  const deleteOutcome = async () => {
    await LearningOutcomesService.deleteLearningOutcome(outcome, programId)
      .then(() => {
        if (outcome.category_id) {
          setCategories((prevCats) => {
            const catIndex = prevCats.findIndex(
              (c) => c.id === outcome.category_id
            );
            prevCats[catIndex].outcomes = prevCats[catIndex].outcomes.filter(
              (o) => o.id !== outcome.id
            );
            return [...prevCats];
          });
        } else {
          setUncategorizedLos((prevLos) =>
            prevLos.filter((o) => o.id !== outcome.id)
          );
        }
        setDeleteOutcomeOpen(false);
        showNotification(
          t("notifications.general.deleteSuccess", {
            obj: t("plo.programLearningOutcome"),
          })
        );
      })
      .catch(() => {
        showNotification(
          t("notifications.general.deleteError", {
            obj: t("plo.programLearningOutcome"),
          }),
          ERROR
        );
      });
  };

  const Versions = ({ past }) => {
    const versions = past ? pastVersions : futureVersions;
    return versions.map((version, key) => (
      <Text color="brand" size="small" key={key}>
        <Flex width="100%" alignItems="start">
          <Flex.Item shouldGrow shouldShrink>
            {version.statement}
          </Flex.Item>
          <Flex.Item>
            {version.effective_term?.start_at
              ? new Date(version.effective_term.start_at).toLocaleDateString()
              : new Date(version.last_updated).toLocaleDateString()}
          </Flex.Item>
        </Flex>
      </Text>
    ));
  };

  return (
    <>
      <Flex wrap="wrap" gap="small">
        <Flex.Item shouldShrink shouldGrow>
          <Flex wrap="wrap" gap="small">
            <Flex.Item>
              <Text weight="bold">{currentVersion.name} </Text>
            </Flex.Item>
            {currentVersion.outcome_variety === "standard" && (
              <Flex.Item>
                <Pill color="info">{t("learningOutcomes.standard")}</Pill>
              </Flex.Item>
            )}
            {outcome.unapproved && (
              <Flex.Item>
                <Pill color="warning">{t("analyze.unapproved")}</Pill>
              </Flex.Item>
            )}
            {!outcome.unapproved &&
              hasUnmappedRatingScale(outcome, ratingScales) && (
                <Flex.Item>
                  <Tooltip
                    renderTip={
                      <div style={{ maxWidth: 450 }}>
                        {t("learningOutcomes.alignedToUnmapped")}
                      </div>
                    }
                    on={["hover", "focus"]}
                    color="primary-inverse"
                  >
                    <IconRubricLine size="x-small" color="warning" />
                  </Tooltip>
                </Flex.Item>
              )}
            {pendingArchive(outcome) && (
              <Flex.Item>
                <Pill>
                  {t("learningOutcomes.archive.archiving")}{" "}
                  {outcome.archive_term.insights_term_id}
                </Pill>
              </Flex.Item>
            )}
          </Flex>
        </Flex.Item>
        <Flex.Item>
          {outcome.unapproved ? (
            <CondensedButton
              size="small"
              onClick={() => setApproveDialog(true)}
            >
              {t("analyze.approve")}
            </CondensedButton>
          ) : (
            <EditOutcomeButton
              outcome={currentVersion}
              handleSave={handleSaveOutcome}
            />
          )}
        </Flex.Item>
        {!outcome.unapproved && (
          <Flex.Item>
            <EditRatingScaleButton
              outcome={outcome}
              setRubricTrigger={setRubricTrigger}
            />
          </Flex.Item>
        )}
        <Flex.Item>
          <Tooltip renderTip={t("learningOutcomes.deleteOutcome")}>
            <IconButton
              withBackground={false}
              withBorder={false}
              size="small"
              renderIcon={IconTrashLine}
              screenReaderLabel={t("learningOutcomes.deleteOutcome")}
              onClick={() => setDeleteOutcomeOpen(true)}
              disabled={outcome.version_history.length > 0}
            />
          </Tooltip>
        </Flex.Item>
        {!outcome.unapproved && (
          <Flex.Item>
            <ArchiveButton outcome={outcome} callback={handleArchiveOutcome} />
          </Flex.Item>
        )}
      </Flex>
      <Flex wrap="wrap" gap="xx-small" padding="xx-small 0 0 0">
        <Flex.Item size="100%">
          <Text size="small">{currentVersion.statement}</Text>
        </Flex.Item>
        {futureVersions.length > 0 && (
          <Flex.Item size="100%">
            <ToggleDetails
              summary={
                <Text size="small">{t("learningOutcomes.futureVersions")}</Text>
              }
            >
              <Versions />
            </ToggleDetails>
          </Flex.Item>
        )}
        {pastVersions.length > 0 && (
          <Flex.Item size="100%">
            <ToggleDetails
              summary={<Text size="small">{t("review.versionHistory")}</Text>}
            >
              <Versions past />
            </ToggleDetails>
          </Flex.Item>
        )}
      </Flex>
      <ApproveDialog
        outcome={outcome}
        open={approveDialog}
        setOpen={setApproveDialog}
        versionOptions={versionOptions}
        progId={programId}
        refresh={setLoadingPlos}
      />
      <DialogYesNo
        message={t("plo.deletePloMessage")}
        openStatus={deleteOutcomeOpen}
        onClickNo={() => setDeleteOutcomeOpen(false)}
        onClickYes={deleteOutcome}
      />
    </>
  );
};

const EditOutcomeButton = ({ outcome, handleSave }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip renderTip={t("learningOutcomes.editOutcome")}>
        <IconButton
          withBackground={false}
          withBorder={false}
          size="small"
          screenReaderLabel={t("learningOutcomes.editOutcome")}
          renderIcon={IconEditLine}
          onClick={() => setOpen(true)}
        />
      </Tooltip>
      <EditOutcomeModal
        open={open}
        setOpen={setOpen}
        outcome={outcome}
        handleSave={handleSave}
      />
    </>
  );
};

const ArchiveButton = ({ outcome, callback }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const disabled = pendingArchive(outcome);

  return (
    <>
      <Tooltip renderTip={t("learningOutcomes.archive.archiveOutcome")}>
        <IconButton
          disabled={disabled}
          withBackground={false}
          withBorder={false}
          size="small"
          screenReaderLabel={t("learningOutcomes.archive.archiveOutcome")}
          renderIcon={IconArchiveLine}
          onClick={() => setOpen(true)}
        />
      </Tooltip>
      <ArchiveModal
        outcome={outcome}
        open={open}
        setOpen={setOpen}
        callback={callback}
      />
    </>
  );
};

const EditRatingScaleButton = ({ outcome, setRubricTrigger }) => {
  const { t } = useTranslation();
  const { ratingScales } = useContext(MasteryLevelContext);
  const [rubricModalOpen, setRubricModalOpen] = useState(false);
  const [currentOutcome, setCurrentOutcome] = useState(outcome);

  const findMappedRatingScale = () => {
    // Find default rating scale, if it exists
    const defaultScale = currentOutcome.rating_scales.find(
      (scale) => scale.is_default
    );
    if (defaultScale) {
      return ratingScales.find(
        (scale) => scale.id === defaultScale.rating_to_mastery_level_map
      );
    }
    // Otherwise, find the first mapped rating scale
    for (let i = 0; i < currentOutcome.rating_scales.length; i++) {
      const mapping = ratingScales.find(
        (mapping) =>
          mapping.id === outcome.rating_scales[i].rating_to_mastery_level_map
      );
      if (mapping && mapping.is_mapped) {
        return mapping;
      }
    }
    return null;
  };

  const mapping = findMappedRatingScale();

  return (
    <>
      <Tooltip renderTip={t("analyze.manageRatingScales")}>
        <IconButton
          withBackground={false}
          withBorder={false}
          size="small"
          screenReaderLabel={t("analyze.manageRatingScales")}
          renderIcon={IconRubricLine}
          onClick={() => setRubricModalOpen(true)}
        />
      </Tooltip>
      <RubricMappingModal
        open={rubricModalOpen}
        setOpen={setRubricModalOpen}
        outcome={currentOutcome}
        setOutcome={setCurrentOutcome}
        setRubricTrigger={setRubricTrigger}
        selectedMapping={mapping}
      />
    </>
  );
};

export default PLOCard;
